import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  EXPORT_CHARGE_ITEM_TYPE,
  GroupResponse,
  IAppError,
  IPatientUser,
  IQuestionnaire,
  QuestionnaireScoring,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, timer } from "rxjs";
import { catchError, first } from "rxjs/operators";
import { QuestionnaireResponse } from "src/app/models/questionnaireResponse.model";
import { Scoring } from "src/app/models/questionnaireScoring.model";
import { BillingService } from "src/app/pages/billing/services/billing.service";
import { QRService } from "src/app/providers/qr-api.service";
import { SessionService } from "src/app/providers/session.service";
import { UserStatisticsService } from "src/app/providers/userStatistics.service";

export enum PDF_TYPE_CONTEXT {
  "QUESTIONNAIRE" = "questionnaire",
  "CHARGE_ITEM" = "chargeItem",
}

@Component({
  selector: "app-pdf-type-choice",
  templateUrl: "./pdf-type-choice.component.html",
  styleUrls: ["./pdf-type-choice.component.scss"],
})
export class PdfTypeChoiceComponent {
  public exportPdfTableInProgress: boolean;
  public exportPdfListInProgress: boolean;
  public PDF_TYPE_CONTEXT = PDF_TYPE_CONTEXT;
  public exportFormats = Object.values(EXPORT_CHARGE_ITEM_TYPE);
  public selectedFormat: EXPORT_CHARGE_ITEM_TYPE;
  public loading: boolean;

  constructor(
    private dialogRef: MatDialogRef<PdfTypeChoiceComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      context: PDF_TYPE_CONTEXT;
      scoring?: Scoring;
      pu?: IPatientUser;
      questionnaire?: IQuestionnaire;
      groupsResponse?: GroupResponse[];
      mergedCategory?: QuestionnaireScoring[][];
      questionnaireResponse?: QuestionnaireResponse;
      chargeItemIds?: string[];
      columns?: string[];
      isAggregatedView?: boolean;
    },
    private snackBar: MatSnackBar,
    private statService: UserStatisticsService,
    private translateService: TranslateService,
    private billingService: BillingService,
    private QRService: QRService,
    private sessionService: SessionService
  ) {}

  public async onExportOneQuestionnaireToPDF(tabular: boolean): Promise<void> {
    if (tabular) {
      this.exportPdfTableInProgress = true;
    } else {
      this.exportPdfListInProgress = true;
    }
    const patientId = this.data.pu?.user?.caremateIdentifier ?? this.data.scoring?.questionnaireResponse?.subject.reference;
    const qrRef = this.data.scoring?.questionnaireResponse?.identifier.value ?? this.data.questionnaireResponse?.identifier.value;
    const orgRef = this.data.pu?.patient?.managingOrganization.reference;
    const lang = this.translateService.currentLang;
    try {
      const response = await this.QRService.exportOneQrInPDF(patientId, qrRef, orgRef, lang, tabular).toPromise();
      const data = "data:application/pdf;base64," + response;

      fetch(data)
        .then((res) => {
          return res.blob();
        })
        .then((blob) => {
          window.open(window.URL.createObjectURL(blob));
          if (this.data.scoring) {
            this.statService.createStatEvent(
              "Export questionnaire responses to PDF (" + this.data.scoring.questionnaireResponse.questionnaire.display + ")"
            );
          }
          this.exportPdfTableInProgress = false;
          this.exportPdfListInProgress = false;
        });
    } catch (error) {
      this.exportPdfTableInProgress = false;
      this.exportPdfListInProgress = false;
      this.snackBar.open(this.translateService.instant("api.errors.pdf-error"), "ok", { duration: 3000 });
      FileLogger.error("PdfTypeChoiceComponent", "Error while getting questionnaire", error);
    }
  }

  public onExportChargeItems(): void {
    this.loading = true;

    combineLatest([
      this.billingService
        .exportChargeItems(
          this.data.chargeItemIds,
          this.selectedFormat,
          [this.sessionService.currentService.reference],
          this.data.columns,
          this.data.isAggregatedView
        )
        .pipe(
          first(),
          catchError((error: IAppError) => {
            FileLogger.error("PdfTypeChoiceComponent", "onExportChargeItems", error);
            throw new Error(error.message);
          })
        ),
      timer(500), // show loader min 500ms
    ]).subscribe(
      ([blob]) => {
        const url = URL.createObjectURL(blob);
        Tools.downloadBlob(blob, url.split("/")[url.split("/").length - 1]);
        this.dialogRef.close();
      },
      (error: IAppError) => {
        FileLogger.error("PdfTypeChoiceComponent", "onExportChargeItems", error);
        throw new Error(error.message);
      },
      () => {
        this.selectedFormat = null;
        this.loading = null;
      }
    );
  }
}
